import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20 max-w-6xl" }
const _hoisted_2 = {
  key: 0,
  class: "mb-6 text-sm text-red-600"
}
const _hoisted_3 = {
  key: 1,
  "data-queue": "",
  class: "mb-6 text-sm text-gray-700 bg-white rounded-lg border border-gray-200 px-4 py-3"
}
const _hoisted_4 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
}
const _hoisted_5 = {
  key: 3,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
}
const _hoisted_6 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm mb-8" }
const _hoisted_7 = { class: "flex flex-wrap items-start justify-between gap-3 p-4 pb-2" }
const _hoisted_8 = { class: "flex-1 min-w-0" }
const _hoisted_9 = { class: "text-sm text-gray-500 mt-0.5" }
const _hoisted_10 = { class: "flex items-center gap-2" }
const _hoisted_11 = { class: "px-4 pb-4 h-72" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "text-gray-700" }
const _hoisted_15 = { class: "text-gray-700 truncate" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = {
  key: 1,
  class: "text-gray-700"
}
const _hoisted_18 = { class: "flex justify-end" }
const _hoisted_19 = { class: "text-sm text-gray-500 mt-2" }

import 'chart.js/auto'

import { computed, onMounted, ref, toRef } from 'vue'
import { Bar } from 'vue-chartjs'
import { useRouter } from 'vue-router'

import EmptyState from '@/components/neue/EmptyState.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueNumberedPagination from '@/components/neue/NeueNumberedPagination.vue'
import NeueTablist from '@/components/neue/NeueTablist.vue'
import NeueWell from '@/components/neue/NeueWell.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import { PAGE_SIZE, useAgentOverview } from '@/composables/useAgentOverview'
import api from '@/store/api'
import AgentConversationsTile from '@/view/ai/AgentConversationsTile.vue'

import {
  activityConfigFor,
  conversationLink,
  dailySeries,
  formatDateTime,
  formatDollars,
  outcomeLabel,
  outcomePillClass,
  queueSentence,
  rate,
  ratio,
  segmentLabel,
  segmentPillClass
} from './agentActivity'


export default {
  __name: 'AgentActivityOverview',
  props: {
  agentId: { type: String, required: true },
  agentType: { type: String, required: true }
},
  setup(__props) {

const props = __props

const router = useRouter()
const config = computed(() => activityConfigFor(props.agentType))

const {
  rangeKey,
  rangeOptions,
  error,
  loadingChart,
  loadingList,
  periods,
  totals,
  tiles,
  rows,
  page,
  pageCount,
  totalCount,
  dateRange,
  goToPage
} = useAgentOverview({ agentId: toRef(props, 'agentId'), endpoint: 'ai_agent_activity' })

const openConversation = row => router.push(conversationLink(row))

// How many customers match the saved settings right now, and how the daily
// cap spreads them. Best effort: the page is still useful without it.
const queue = ref(null)
onMounted(() =>
  api
    .call('GET', `agents/${props.agentId}/impact`, {}, { receive: false, save: false })
    .then(response => {
      queue.value = queueSentence(response.data?.data?.attributes)
    })
    .catch(() => {})
)

// The reply is free text, so it gets the room; the rest size to their content.
const columns = computed(() =>
  [
    { width: 'minmax(140px, 1.2fr)' },
    { width: 'minmax(100px, 1fr)' },
    config.value.showSegment ? { width: 'minmax(110px, 0.8fr)' } : null,
    { width: 'minmax(160px, 2.5fr)' },
    { width: 'minmax(120px, 0.9fr)' },
    config.value.hasRevenue ? { width: 'minmax(80px, 0.7fr)' } : null,
    { width: '64px' }
  ].filter(Boolean)
)

const chartData = computed(() => {
  if (!periods.value.length && totals.value.conversations === 0) return null

  const days = dailySeries(periods.value, dateRange.value)
  const datasets = [
    { label: 'Conversations started', backgroundColor: '#6366f1', yAxisID: 'y', data: days.map(d => d.conversations) },
    { label: 'With a reply', backgroundColor: '#0ea5e9', yAxisID: 'y', data: days.map(d => d.replied) },
    { label: config.value.successLabel, backgroundColor: '#10b981', yAxisID: 'y', data: days.map(d => d.successes) }
  ]
  if (config.value.hasRevenue) {
    datasets.push({
      label: 'Revenue ($)',
      backgroundColor: '#f59e0b',
      yAxisID: 'y1',
      data: days.map(d => (d.revenue_cents || 0) / 100)
    })
  }
  return { labels: days.map(d => formatChartLabel(d.date)), datasets }
})

const formatChartLabel = iso => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

const chartOptions = computed(() => {
  const scales = {
    y: { beginAtZero: true, position: 'left', ticks: { precision: 0 }, title: { display: true, text: 'Count' } }
  }
  if (config.value.hasRevenue) {
    scales.y1 = {
      beginAtZero: true,
      position: 'right',
      title: { display: true, text: 'Revenue ($)' },
      grid: { drawOnChartArea: false }
    }
  }
  return { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales }
})

const formatPercent = share => (share === null ? '-' : `${Math.round(share * 100)}%`)
const formatWholeDollars = cents => `$${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

const replyRate = period => rate(period.replied_sessions, period.conversations)
const successRate = period => rate(period.successes, period.conversations)

const rateDelta = (current, prior) => (current === null || prior === null ? null : ratio(current, prior))

const tileList = computed(() => {
  if (!tiles.value) return []
  const { current, prior, period_days: days } = tiles.value
  const lastTile = config.value.hasRevenue
    ? {
        label: config.value.revenueLabel,
        value: formatWholeDollars(current.revenue_cents),
        delta: ratio(current.revenue_cents, prior.revenue_cents)
      }
    : {
        label: 'Review Rate',
        value: formatPercent(successRate(current)),
        delta: rateDelta(successRate(current), successRate(prior)),
        caption: 'of conversations started'
      }

  return [
    {
      label: `Conversations · ${days}D`,
      value: Number(current.conversations).toLocaleString(),
      delta: ratio(current.conversations, prior.conversations)
    },
    {
      label: 'Reply Rate',
      value: formatPercent(replyRate(current)),
      delta: rateDelta(replyRate(current), replyRate(prior)),
      caption: `${current.replied_sessions} replied`
    },
    {
      label: config.value.successLabel,
      value: Number(current.successes).toLocaleString(),
      delta: ratio(current.successes, prior.successes)
    },
    lastTile
  ]
})

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(error))
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_unref(error)), 1))
      : _createCommentVNode("", true),
    (queue.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(queue.value), 1))
      : _createCommentVNode("", true),
    (!_unref(tiles) && _unref(loadingChart))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (n) => {
            return _createElementVNode("div", {
              key: n,
              class: "bg-white rounded-lg border border-gray-200 p-6 h-32 animate-pulse"
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "h-3 w-24 bg-gray-200 rounded mb-3" }, null, -1),
              _createElementVNode("div", { class: "h-8 w-20 bg-gray-300 rounded" }, null, -1)
            ]))])
          }), 64))
        ]))
      : _createCommentVNode("", true),
    (_unref(tiles))
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tileList.value, (tile) => {
            return (_openBlock(), _createBlock(AgentConversationsTile, {
              key: tile.label,
              label: tile.label,
              value: tile.value,
              delta: tile.delta,
              caption: tile.caption
            }, null, 8, ["label", "value", "delta", "caption"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createVNode(NeueLoading, { loading: _unref(loadingChart) }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "text-lg font-bold text-gray-900" }, "Activity", -1)),
              _createElementVNode("p", _hoisted_9, [
                _createElementVNode("strong", null, _toDisplayString(_unref(totals).conversations), 1),
                _cache[5] || (_cache[5] = _createTextVNode(" conversations started · ", -1)),
                _createElementVNode("strong", null, _toDisplayString(_unref(totals).replied || 0), 1),
                _cache[6] || (_cache[6] = _createTextVNode(" with a reply · ", -1)),
                _createElementVNode("strong", null, _toDisplayString(_unref(totals).successes || 0), 1),
                _createTextVNode(" " + _toDisplayString(config.value.successLabel.toLowerCase()) + " ", 1),
                (config.value.hasRevenue)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _cache[3] || (_cache[3] = _createTextVNode(" · ", -1)),
                      _createElementVNode("strong", null, _toDisplayString(_unref(formatDollars)(_unref(totals).revenue_cents)), 1),
                      _cache[4] || (_cache[4] = _createTextVNode(" revenue ", -1))
                    ], 64))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createVNode(SelectInput, {
                modelValue: _unref(rangeKey),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => (_isRef(rangeKey) ? (rangeKey).value = $event : null)),
                slim: "",
                options: _unref(rangeOptions).map(opt => ({ name: opt.label, value: opt.value }))
              }, null, 8, ["modelValue", "options"])
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            (chartData.value)
              ? (_openBlock(), _createBlock(_unref(Bar), {
                  key: 0,
                  data: chartData.value,
                  options: chartOptions.value
                }, null, 8, ["data", "options"]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(NeueLoading, {
      loading: _unref(loadingList) && !_unref(rows).length
    }, {
      default: _withCtx(() => [
        (_unref(rows).length)
          ? (_openBlock(), _createBlock(NeueTablist, { key: 0 }, {
              head: _withCtx(() => [
                _createVNode(TablistHeader, null, {
                  default: _withCtx(() => [
                    _createVNode(TablistGrid, {
                      columns: columns.value,
                      header: ""
                    }, {
                      default: _withCtx(() => [
                        _cache[8] || (_cache[8] = _createElementVNode("div", null, "Conversation Date", -1)),
                        _cache[9] || (_cache[9] = _createElementVNode("div", null, "Customer", -1)),
                        (config.value.showSegment)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, "Segment"))
                          : _createCommentVNode("", true),
                        _cache[10] || (_cache[10] = _createElementVNode("div", null, "Reply", -1)),
                        _cache[11] || (_cache[11] = _createElementVNode("div", null, "Outcome", -1)),
                        (config.value.hasRevenue)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, "Revenue"))
                          : _createCommentVNode("", true),
                        _cache[12] || (_cache[12] = _createElementVNode("div", null, null, -1))
                      ]),
                      _: 1
                    }, 8, ["columns"])
                  ]),
                  _: 1
                })
              ]),
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rows), (row) => {
                  return (_openBlock(), _createBlock(TablistRow, {
                    key: row.id,
                    class: "cursor-pointer",
                    onClick: $event => (openConversation(row))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(TablistGrid, {
                        columns: columns.value,
                        class: "items-center"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(formatDateTime)(row.attributes.conversation_date)), 1),
                          _createElementVNode("div", _hoisted_15, _toDisplayString(row.attributes.customer_name || '-'), 1),
                          (config.value.showSegment)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _createElementVNode("span", {
                                  class: _normalizeClass(["px-2 text-xs leading-5 font-semibold rounded-full whitespace-nowrap", _unref(segmentPillClass)(row.attributes.segment)])
                                }, _toDisplayString(_unref(segmentLabel)(row.attributes.segment)), 3)
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: _normalizeClass(["truncate", row.attributes.replied ? 'text-gray-700' : 'text-gray-400'])
                          }, _toDisplayString(row.attributes.customer_reply || 'No reply yet'), 3),
                          _createElementVNode("div", null, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["px-2 text-xs leading-5 font-semibold rounded-full whitespace-nowrap", _unref(outcomePillClass)(row.attributes.outcome)])
                            }, _toDisplayString(_unref(outcomeLabel)(row.attributes.outcome)), 3)
                          ]),
                          (config.value.hasRevenue)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, _toDisplayString(_unref(formatDollars)(row.attributes.revenue_added_cents)), 1))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_18, [
                            _createVNode(_component_router_link, {
                              to: _unref(conversationLink)(row),
                              class: "text-sm font-medium text-brand-600 hover:text-brand-700 whitespace-nowrap",
                              onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                            }, {
                              default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                _createTextVNode(" View → ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["to"])
                          ])
                        ]),
                        _: 2
                      }, 1032, ["columns"])
                    ]),
                    _: 2
                  }, 1032, ["onClick"]))
                }), 128))
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(NeueWell, {
              key: 1,
              class: "text-center"
            }, {
              default: _withCtx(() => [
                _createVNode(EmptyState, { plural: "conversations" }),
                _createElementVNode("p", _hoisted_19, _toDisplayString(config.value.emptyText), 1)
              ]),
              _: 1
            }))
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(NeueNumberedPagination, {
      page: _unref(page),
      "page-count": _unref(pageCount),
      "total-count": _unref(totalCount),
      "page-size": _unref(PAGE_SIZE),
      loading: _unref(loadingList),
      onChange: _unref(goToPage)
    }, null, 8, ["page", "page-count", "total-count", "page-size", "loading", "onChange"])
  ]))
}
}

}